// src/components/reporting/cohort/CohortCribSheet.js
// The Cohort Crib Sheet — a coach's at-a-glance prep sheet for one cohort.
//
//   GET /api/v1/reports/cohortcheatsheet/:cohortId   (routes/v1/cohort-cheat-sheet.js)
//
// One row per participant, already sorted by name and already carrying the
// API's two sentinel strings ("No case studies", "no debrief completed") —
// this component renders what it is given rather than recomputing any of it.
//
// Layout follows My Active Conversations (components/conversations/ConversationsPage.js):
// list-header, then a search bar, then the shared .arp-table. CSV and print are
// both client-side — the report is small and already loaded, so neither needs
// a round trip.
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FileCsv, FilePdf } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiCall } from '../../../hooks/useApiCall';
import apiService from '../../../services/apiService';
import '../../../styles/conversations.css';
import '../../../styles/activityReporting.css';
import '../../../styles/cohortCribSheet.css';

// Column order is the spec's, and the CSV mirrors it exactly.
const COLUMNS = [
  { key: 'participant',           label: 'Participant', className: 'ccs-col-participant' },
  { key: 'conversationTitle',     label: 'Case Study',  className: 'ccs-col-casestudy' },
  { key: 'realIssue',             label: 'Real Issue',  className: 'ccs-col-prose' },
  { key: 'counterpart',           label: 'Counterpart', className: 'ccs-col-prose' },
  { key: 'challenge',             label: 'Challenge',   className: 'ccs-col-prose' },
  { key: 'highestLeverageChange', label: 'Leverage',    className: 'ccs-col-prose' },
];

// The API's sentinels, echoed here only to style them as notes rather than
// prose. Any other value is participant content and renders plainly.
const SENTINELS = new Set(['No case studies', 'no debrief completed']);

const escapeCsv = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows) => {
  const header = COLUMNS.map((c) => escapeCsv(c.label)).join(',');
  const body = rows.map((r) => COLUMNS.map((c) => escapeCsv(r[c.key])).join(','));
  return [header, ...body].join('\r\n');
};

const slugify = (s) => (s || 'cohort').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const CohortCribSheet = ({ cohortId, cohortName }) => {
  const { user } = useAuth();
  const { loading, error, makeRequest } = useApiCall();

  const [cohort, setCohort] = useState(null);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!cohortId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await makeRequest(
          () => apiService.get(`/api/v1/reports/cohortcheatsheet/${cohortId}`, user),
          'Loading crib sheet...'
        );
        if (cancelled || !data.success) return;
        setCohort(data.data.cohort || null);
        setRows(data.data.rows || []);
        setTruncated(Boolean(data.data.truncated));
        setGeneratedAt(data.data.generatedAt || null);
      } catch (err) {
        console.error('Error loading cohort crib sheet:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  // Search narrows on participant name only — the prose columns are long
  // enough that matching them would make almost every term match every row.
  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => (r.participant || '').toLowerCase().includes(term));
  }, [rows, searchTerm]);

  const displayName = cohort?.name || cohortName || 'Cohort';

  const handleDownloadCsv = useCallback(() => {
    const blob = new Blob([toCsv(visibleRows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crib-sheet-${slugify(displayName)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [visibleRows, displayName]);

  const renderCell = (row, column) => {
    const value = row[column.key];
    if (value === null || value === undefined || value === '') {
      return <span className="ccs-empty-note">—</span>;
    }
    if (SENTINELS.has(value)) {
      return <span className="ccs-empty-note">{value}</span>;
    }
    return value;
  };

  return (
    <div className="ccs-page">
      {/* Rendered only into the printed document — on screen this information
          lives in the title bar, which print hides along with the search box. */}
      <div className="ccs-print-header">
        <h1 className="ccs-print-title">Cohort Crib Sheet — {displayName}</h1>
        <p className="ccs-print-meta">
          {rows.length} participant{rows.length === 1 ? '' : 's'}
          {generatedAt ? ` · generated ${new Date(generatedAt).toLocaleString()}` : ''}
        </p>
      </div>

      <div className="list-header ccs-header">
        <h1>Cohort Crib Sheet</h1>
        {!loading && (
          <p className="conversation-count">
            {displayName} · {visibleRows.length} of {rows.length} participant{rows.length === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <div className="list-controls ccs-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search participants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')} title="Clear search">
              ✕
            </button>
          )}
        </div>

        <div className="ccs-actions">
          <button
            type="button"
            className="ccs-icon-btn"
            onClick={handleDownloadCsv}
            disabled={loading || visibleRows.length === 0}
            title="Download as CSV"
            aria-label="Download as CSV"
          >
            <FileCsv size={18} weight="light" />
          </button>
          <button
            type="button"
            className="ccs-icon-btn"
            onClick={() => window.print()}
            disabled={loading || rows.length === 0}
            title="Print / Save as PDF"
            aria-label="Print or save as PDF"
          >
            <FilePdf size={18} weight="light" />
          </button>
        </div>
      </div>

      {error && <div className="arp-error-state">{error}</div>}

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="arp-state">This cohort has no participants yet.</div>
      ) : visibleRows.length === 0 ? (
        <div className="arp-state">No participants match your search.</div>
      ) : (
        <>
          <div className="arp-table-wrapper ccs-table-wrapper">
            <table className="arp-table ccs-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={c.className}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.userId}>
                    {COLUMNS.map((c) => (
                      <td key={c.key} className={c.className}>{renderCell(row, c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {truncated && (
            <p className="ccs-truncated-note">
              Showing the first {rows.length} participants — this cohort is larger than the report&apos;s limit.
            </p>
          )}
        </>
      )}
    </div>
  );
};

CohortCribSheet.propTypes = {
  cohortId: PropTypes.string.isRequired,
  cohortName: PropTypes.string,
};

export default CohortCribSheet;
